import { rulesEngineGet, rulesEngineRequest } from './rulesEngineClient';
import { getAccessManagementOrganizationsUrl, getAccessManagementUsersUrl } from './rulesEngineEndpoints';

const asText = (value) => {
  if (value === null || value === undefined) {
    return '';
  }

  if (typeof value === 'string') {
    return value.trim();
  }

  if (typeof value === 'number' || typeof value === 'boolean') {
    return String(value);
  }

  return '';
};

// Allowlist explicita: o endpoint do AMS devolve o `User` cru, incluindo
// `password_digest`, que nunca pode vazar para o estado do componente.
export const normalizeAmsUser = (user, index = 0) => ({
  id: asText(user?.id || user?._id) || `ams-user-${index}`,
  externalId: asText(user?.external_id),
  name: asText(user?.name),
  email: asText(user?.email),
  role: asText(user?.role),
  organizationId: asText(user?.organization_id),
  clientId: asText(user?.client_id),
  status: asText(user?.status) || 'active',
  tokenVersion: Number(user?.token_version) || 0,
  passwordRotatedAt: asText(user?.password_rotated_at),
  createdAt: asText(user?.created_at),
  updatedAt: asText(user?.updated_at),
});

// `id` usa o external_id, não o _id do Mongo: é o external_id que
// User#organization_id/Role#organization_id de fato referenciam em todo o
// domínio do AMS — usar o _id aqui faria qualquer comparação (filtros,
// atribuição de organização a um usuário/papel) nunca bater.
export const normalizeAmsOrganization = (organization, index = 0) => {
  const externalId = asText(organization?.external_id);

  return {
    id: externalId || `ams-org-${index}`,
    externalId,
    // _id interno do Mongo no AMS -- necessario pra atualizar (PATCH
    // /organizations/:id usa esse id, nao o external_id). Nao usar pra
    // nenhuma outra finalidade (ver comentario acima sobre `id`/`externalId`).
    amsId: asText(organization?.id || organization?._id),
    name: asText(organization?.name),
    status: asText(organization?.status) || 'active',
  };
};

const buildAmsUserPayload = (payload = {}) => ({
  ...(payload.externalId ? { external_id: payload.externalId } : {}),
  ...(payload.name ? { name: payload.name } : {}),
  ...(payload.email ? { email: payload.email } : {}),
  ...(payload.role ? { role: payload.role } : {}),
  ...(payload.organizationId ? { organization_id: payload.organizationId } : {}),
  ...(payload.clientId ? { client_id: payload.clientId } : {}),
  ...(payload.status ? { status: payload.status } : {}),
});

const extractAmsCollection = (response, key) => {
  if (Array.isArray(response)) {
    return response;
  }

  return Array.isArray(response?.[key]) ? response[key] : [];
};

export const getAccessManagementUsers = async () => {
  const response = await rulesEngineGet(getAccessManagementUsersUrl());
  const users = extractAmsCollection(response, 'users');

  return users.map((user, index) => normalizeAmsUser(user, index));
};

export const createAccessManagementUser = async (payload = {}) => {
  const response = await rulesEngineRequest({
    method: 'post',
    url: getAccessManagementUsersUrl(),
    data: { user: buildAmsUserPayload(payload) },
  });

  return normalizeAmsUser(response?.data ?? response);
};

export const updateAccessManagementUser = async (userId, payload = {}) => {
  if (!userId) {
    throw new Error('userId is required to update AMS user');
  }

  const response = await rulesEngineRequest({
    method: 'patch',
    url: getAccessManagementUsersUrl(userId),
    data: { user: buildAmsUserPayload(payload) },
  });

  return normalizeAmsUser(response?.data ?? response);
};

export const changeAccessManagementUserRole = async (userId, role) => {
  if (!userId) {
    throw new Error('userId is required to change AMS user role');
  }

  const response = await rulesEngineRequest({
    method: 'patch',
    url: getAccessManagementUsersUrl(`${userId}/role`),
    data: { role },
  });

  return normalizeAmsUser(response?.data ?? response);
};

export const setAccessManagementUserCredentials = async (userId, password) => {
  if (!userId) {
    throw new Error('userId is required to set AMS user credentials');
  }

  const response = await rulesEngineRequest({
    method: 'patch',
    url: getAccessManagementUsersUrl(`${userId}/credentials`),
    data: { user: { password, password_confirmation: password } },
  });

  return normalizeAmsUser(response?.data ?? response);
};

export const suspendAccessManagementUser = async (userId, reason) => {
  if (!userId) {
    throw new Error('userId is required to suspend AMS user');
  }

  const response = await rulesEngineRequest({
    method: 'post',
    url: getAccessManagementUsersUrl(`${userId}/suspend`),
    data: reason ? { reason } : {},
  });

  return normalizeAmsUser(response?.data ?? response);
};

export const activateAccessManagementUser = async (userId, reason) => {
  if (!userId) {
    throw new Error('userId is required to activate AMS user');
  }

  const response = await rulesEngineRequest({
    method: 'post',
    url: getAccessManagementUsersUrl(`${userId}/activate`),
    data: reason ? { reason } : {},
  });

  return normalizeAmsUser(response?.data ?? response);
};

// Exposto por completude de cobertura do endpoint; sem botão na UI hoje.
export const deleteAccessManagementUser = async (userId, reason) => {
  if (!userId) {
    throw new Error('userId is required to delete AMS user');
  }

  await rulesEngineRequest({
    method: 'delete',
    url: getAccessManagementUsersUrl(userId),
    data: reason ? { reason } : {},
  });
};

export const getAccessManagementOrganizations = async () => {
  const response = await rulesEngineGet(getAccessManagementOrganizationsUrl());
  const organizations = extractAmsCollection(response, 'organizations');

  return organizations.map((organization, index) => normalizeAmsOrganization(organization, index));
};

const buildAmsOrganizationPayload = (payload = {}) => ({
  ...(payload.externalId ? { external_id: payload.externalId } : {}),
  ...(payload.name ? { name: payload.name } : {}),
  ...(payload.status ? { status: payload.status } : {}),
});

// externalId deve ser o id da Compliance::Organization no rules-engine --
// e' assim que User#organization_id/Role#organization_id do AMS referenciam
// a organizacao (ver normalizeAmsOrganization acima).
export const createAccessManagementOrganization = async (payload = {}) => {
  const response = await rulesEngineRequest({
    method: 'post',
    url: getAccessManagementOrganizationsUrl(),
    data: { organization: buildAmsOrganizationPayload(payload) },
  });

  return normalizeAmsOrganization(response?.data ?? response);
};

export const updateAccessManagementOrganization = async (amsOrganizationId, payload = {}) => {
  if (!amsOrganizationId) {
    throw new Error('amsOrganizationId is required to update AMS organization');
  }

  const response = await rulesEngineRequest({
    method: 'patch',
    url: getAccessManagementOrganizationsUrl(amsOrganizationId),
    data: { organization: buildAmsOrganizationPayload(payload) },
  });

  return normalizeAmsOrganization(response?.data ?? response);
};
